import Vue from 'vue';
import _ from "lodash";
import i18n from '../../i18n';
import { ADD_TOAST_MESSAGE } from "vuex-toast";
import chartAPI from "../api/chart";
import userAPI from "../api/user";
import common from '../../libs/common';
import portfolioAPI from '../api/portfolio';

const mapMOS = {
  TITLE_00: 'great',
  TITLE_01: 'good',
  TITLE_02: 'fair',
  TITLE_03: 'poor'
};

const mapBusinessType = {
  TITLE_00: 'def',
  TITLE_01: 'cyc',
};

const mapBeta = {
  TITLE_00: 'high',
  TITLE_01: 'low',
};

const mapDCA = {
  TITLE_00: 'high',
  TITLE_01: 'low',
};

const mapDonutChart = {
  mos: 'MOS',
  dca: 'DCA',
  beta: 'Beta',
  sector: 'Sector',
  business_type: 'Type',
};

const types = {
  LOADING: 'LOADING',
  LOADING_BY_KEY: "LOADING_BY_KEY",
  SET_FIELD: 'SET_FIELD',
  SET_SORT_FIELD: 'SET_SORT_FIELD',
  SET_SORT_WL_FIELD: 'SET_SORT_WL_FIELD',
  ACTION_WATCH_LIST_SUCCESS: 'ACTION_WATCH_LIST_SUCCESS',
  GET_WATCH_LIST_SUCCESS: 'GET_WATCH_LIST_SUCCESS',
  GET_WATCH_LIST_FAIL: 'GET_WATCH_LIST_FAIL',
  GET_DONUT_CHART_SUCCESS: 'GET_DONUT_CHART_SUCCESS',
  GET_DONUT_CHART_FAIL: 'GET_DONUT_CHART_FAIL',
  GET_PORTFOLIOS_OK: 'GET_PORTFOLIOS_OK',
  GET_PORTFOLIOS_FAILED: 'GET_PORTFOLIOS_FAILED'
};

// initial state
const initalState = {
  loading: false,
  form: {},
  sort: {
    field: "",
    order: ""
  },
  sortWL: {
    field: "mos",
    order: "desc"
  },
  vplist: {},
  item: {},
  industry: {},
  watchlist: {},
  donutChartMOS: {
    series: [],
    chartOptions: {
      chart: {
        type: 'donut',
        height: 320
      },
      legend: {
        position: 'bottom'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 320
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      colors: ['var(--purple)', 'var(--green)', 'var(--yellow)', 'var(--red)'],
    }
  },
  donutChartDCA: {
    series: [],
    chartOptions: {
      chart: {
        type: 'donut',
        height: 320
      },
      legend: {
        position: 'bottom'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 320
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      colors: ['var(--green)', 'var(--red)']
    }
  },
  donutChartBeta: {
    series: [],
    chartOptions: {
      chart: {
        type: 'donut',
        height: 320
      },
      legend: {
        position: 'bottom'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 320
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      colors: ['var(--green)', 'var(--red)']
    }
  },
  donutChartBusinessType: {
    series: [],
    chartOptions: {
      chart: {
        type: 'donut',
        height: 320
      },
      legend: {
        position: 'bottom'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 320
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      colors: ['var(--green)', 'var(--red)']
    }
  },
  donutChartSector: {
    series: [],
    chartOptions: {
      chart: {
        type: 'donut',
        height: 400
      },
      legend: {
        position: 'bottom'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 350
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      colors: ['var(--purple)', 'var(--green)', 'var(--yellow)', 'var(--cyan)', 'var(--red)',
        'var(--orange)', 'var(--teal)', 'var(--indigo)', 'var(--blue)', 'var(--purple-dark)',
        'var(--pink)', 'var(--dark)'
      ]
    }
  },
};

// getters
const getters = {
  form: state => state.form,
  sort: state => state.sort,
  sortWL: state => state.sortWL,
  loading: state => state.loading,
  item: state => state.item,
  industry: state => state.industry,
  filterPayload: state => state.filterPayload,
  watchlist: state => state.watchlist,
  donutChartMOS: state => state.donutChartMOS,
  donutChartDCA: state => state.donutChartDCA,
  donutChartBeta: state => state.donutChartBeta,
  donutChartBusinessType: state => state.donutChartBusinessType,
  donutChartSector: state => state.donutChartSector,
  vplist: state => state.vplist,
};

// actions
const actions = {
  setField({ commit }, { key, value }) {
    commit(types.SET_FIELD, { key, value });
  },

  setSortField({ commit }, { key, value }) {
    commit(types.SET_SORT_FIELD, { key, value });
  },

  setSortWLField({ commit }, { key, value }) {
    commit(types.SET_SORT_WL_FIELD, { key, value });
  },

  async createPortfolio({ commit, dispatch }, formData) {
    try {
      await portfolioAPI(dispatch).create(formData);
      dispatch("getPortfolios");
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.login_ok'), type: 'success' },
        { root: true }
      );
    } catch (e) {
      Vue.$log.error("Failed to create the portfolio", e);
    }
  },

  async removePortfolio({ commit, dispatch }, portfolioId) {
    try {
      await portfolioAPI(dispatch).remove(portfolioId);
      dispatch("getPortfolios");
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.portfolio_remove_ok'), type: 'success' },
        { root: true }
      );
      this.$router.push({ name: 'index' });
    } catch (e) {
      Vue.$log.error("Failed to remove the portfolio", e);
    }
  },

  async getPortfolios({ commit, dispatch }) {
    try {
      commit('LOADING_BY_KEY', { key: "portfolioListLoading", status: true });
      const { data } = await portfolioAPI(dispatch).getList();
      commit(types.GET_PORTFOLIOS_OK, data);
    } catch (e) {
      Vue.$log.error("Get portfolios failed", e);
      commit(types.GET_PORTFOLIOS_FAILED);
      commit('LOADING_BY_KEY', { key: "portfolioListLoading", status: false });
    }
  },

  async filterWatchlist({ commit, dispatch }, payload) {
    try {
      commit('LOADING_BY_KEY', { key: "watchlistLoading", status: true });
      const { data } = await userAPI(dispatch).filterWatchlist(payload);
      commit(types.GET_WATCH_LIST_SUCCESS, data);
    } catch (e) {
      Vue.$log.error(i18n.t('messages.watchlist_remove_failed'), e);
      commit(types.GET_WATCH_LIST_FAIL, data);
      commit('LOADING_BY_KEY', { key: "watchlistLoading", status: false });
    }
  },

  async onRemoveWatchlist({ commit, dispatch }, req) {
    try {
      commit('LOADING_BY_KEY', { key: "stockWatchlistLoading", status: true });
      const formData = {
        codes: [req.code]
      };
      await userAPI(dispatch).onRemoveWatchlist(formData);
      dispatch("filterWatchlist", req.payload);
      dispatch(
        ADD_TOAST_MESSAGE,
        {
          text: i18n.t('messages.watchlist_remove_ok'),
          type: "success"
        },
        { root: true }
      );
      commit(types.ACTION_WATCH_LIST_SUCCESS, false);
    } catch (e) {
      Vue.$log.error(i18n.t('messages.watchlist_remove_failed'), e);
      commit('LOADING_BY_KEY', { key: "stockWatchlistLoading", status: false });
    }
  },

  async getDonutChartByType({ commit, dispatch }, params) {
    try {
      commit('LOADING_BY_KEY', { key: `donut${mapDonutChart[params.method]}Loading`, status: true });
      const { data } = await chartAPI(dispatch).getDonutChartByType(params.method);
      commit(types.GET_DONUT_CHART_SUCCESS, { data: data, method: params.method });
    } catch (e) {
      Vue.$log.error("Get donut chart failed", e);
      commit(types.GET_DONUT_CHART_FAIL, params.method);
      commit('LOADING_BY_KEY', { key: `donut${mapDonutChart[params.method]}Loading`, status: false });
    }
  },
};

// mutations
const mutations = {
  [types.GET_WATCH_LIST_SUCCESS](state, data) {
    Vue.set(state, 'watchlist', data);
    Vue.set(state.form, 'watchlistLoading', false);
  },

  [types.ACTION_WATCH_LIST_SUCCESS](state, data) {
    Vue.set(state.item, 'favorite', data);
    Vue.set(state.form, 'stockWatchlistLoading', false);
  },

  [types.GET_DONUT_CHART_SUCCESS](state, res) {
    let titles = [];
    switch (res.method) {
      case 'mos':
        res.data.titles.map(item => {
          let key = mapMOS[item];
          titles.push(i18n.t(`stocks.mos.${key}`))
        });
        Vue.set(state.donutChartMOS, "series", res.data.values);
        Vue.set(state.donutChartMOS.chartOptions, "labels", titles);
        break;
      case 'dca':
        res.data.titles.map(item => {
          let key = mapDCA[item];
          titles.push(i18n.t(`stocks.dca.${key}`))
        });
        Vue.set(state.donutChartDCA, "series", res.data.values);
        Vue.set(state.donutChartDCA.chartOptions, "labels", titles);
        break;
      case 'beta':
        res.data.titles.map(item => {
          let key = mapBeta[item];
          titles.push(i18n.t(`beta_types.${key}`))
        });
        Vue.set(state.donutChartBeta, "series", res.data.values);
        Vue.set(state.donutChartBeta.chartOptions, "labels", titles);
        break;
      case 'business_type':
        res.data.titles.map(item => {
          let key = mapBusinessType[item];
          titles.push(common.getBusinessTypeText(key));
        });
        Vue.set(state.donutChartBusinessType, "series", res.data.values);
        Vue.set(state.donutChartBusinessType.chartOptions, "labels", titles);
        break;
      case 'sector':
        res.data.titles.map(item => {
          titles.push(common.getSectorText(item))
        });
        Vue.set(state.donutChartSector, "series", res.data.values);
        Vue.set(state.donutChartSector.chartOptions, "labels", titles);
        break;
      default:
        break;
    }
    Vue.set(state.form, `donut${mapDonutChart[res.method]}Loading`, false);
  },

  [types.GET_DONUT_CHART_FAIL](state, method) {
    Vue.set(state.form, `donut${mapDonutChart[method]}Loading`, false);
  },

  [types.SET_FIELD](state, params) {
    Vue.set(state.form, params.key, params.value);
  },

  [types.SET_SORT_FIELD](state, params) {
    Vue.set(state.sort, params.key, params.value);
  },

  [types.SET_SORT_WL_FIELD](state, params) {
    Vue.set(state.sortWL, params.key, params.value);
  },

  [types.GET_PORTFOLIOS_OK](state, data) {
    Vue.set(state, 'vplist', data);
    Vue.set(state.form, 'portfolioListLoading', false);
  },
  [types.GET_PORTFOLIOS_FAILED](state) {
    Vue.set(state.form, 'portfolioListLoading', false);
  },

  [types.LOADING](state, loading) {
    Vue.set(state, 'loading', loading);
  },

  [types.LOADING_BY_KEY](state, data) {
    Vue.set(state.form, data.key, data.status);
  },
};

export default {
  namespaced: true,
  state: initalState,
  getters,
  actions,
  mutations,
};
